<template>
    <div class="flex">
        <account-picker
            v-model:account-no="line.debitAccount"
            v-model:sub-account-uuid="line.debitSubAccount"
            v-model:sub-account-lines="line.subAccountLines"
            :accounts
            class="w-full min-w-[90px] max-w-[300px]"
            :class="[isVatLiable ? 'min-w-[90px] max-w-[300px]': 'min-w-[150px] max-w-[420px]']"
            data-cy="debit-account-input"
            @update:account-no="onAccountUpdate"
            @on-sub-account-selection-complete="onSubAccountSelectionComplete"
        />
        <vat-picker
            v-if="isVatLiable"
            v-model:vat-option="vatOption"
            :amount
            :editable="isVatEditable"
            class="w-full min-w-[60px] max-w-[120px]"
            data-cy="debit-vat-input"
        />
    </div>
</template>

<script setup lang="ts">
import {TransactionEntryLine} from '@/types/transaction/TransactionEntryLine'
import AccountPicker from '@/account-picker/AccountPicker.vue'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import {findSubAccountByUuid, useSubAccounts} from '@/subaccount/subAccount.service'
import {SubAccountLine} from '@/subaccount/outstandingBalance/SubAccountLine'
import {useAccounts} from '@/services/account.service'
import {isDebitVatEditable, useVatService} from '@/vat/vat.service'
import VatPicker from '@/vat/VatPicker.vue'
import {computed, ComputedRef} from 'vue'
import {useSetting} from '@/setting/setting.service'
import {VatOption, defaultVatOption} from '@/vat/types/VatOptions'

const line = defineModel<TransactionEntryLine>({required: true})
const accountStore = useAccounts()
const {customers, suppliers} = useSubAccounts()
const {vatOptions} = useVatService()

const {setting} = useSetting<boolean>('VAT_LIABLE')

const accounts = computed(() => {
    return [...bookkeepingAccounts.value, ...customers.value, ...suppliers.value]
        .filter(acc => acc.active)
})

const isVatLiable: ComputedRef<Boolean> = computed<Boolean>(() => {
    const isVatLiableSetting = setting.value
    return isVatLiableSetting?.value ?? isVatLiableSetting?.default ?? isVatLiable.value
})

const onAccountUpdate = (accountNo: string | undefined) => {
    if (!accountNo) {
        line.value.vatOptionIdDebit = defaultVatOption.id
        return
    }
    if (isVatLiable.value) {
        const vatOptionDebitId = accountStore.findAccountOptionFrom(accountNo, line.value.debitSubAccount)
        line.value.vatOptionIdDebit = vatOptionDebitId?.vatSuggestionId || defaultVatOption.id
    }
}

//TODO: Clean up
const onSubAccountSelectionComplete = (posts: SubAccountLine[]) => {
    setTransactionLineAmount(posts)
    if (line.value.debitSubAccount) {
        const subAccount = findSubAccountByUuid(line.value.debitSubAccount)
        if (subAccount && subAccount.bban) {
            line.value.bban = subAccount.bban
        }

        if (posts.length === 1) {
            const subAccountLine = posts[0]
            line.value.invoiceUuid = subAccountLine.invoiceUuid
            line.value.kid = subAccountLine.kid
            line.value.invoiceNo = subAccountLine.invoiceNo
            line.value.bban = subAccountLine.bankAccountNo || subAccount.bban || subAccount.iban
        } else {
            line.value.bban = subAccount.bban || subAccount.iban || posts.find(post => post.bankAccountNo)?.bankAccountNo
        }
    }
}

const setTransactionLineAmount = (posts: SubAccountLine[]) => {
    line.value.amount = Math.abs(posts.reduce((acc, curr) => acc + curr.remainingAmount, 0))
}

const amount = computed(() => line.value.amount)

const vatOption = computed<VatOption | undefined>({
    get: () => vatOptions.value.find((option) => option.id === line.value.vatOptionIdDebit),
    set: (newVatOption: VatOption | undefined) => {
        if (!newVatOption) return
        line.value.vatOptionIdDebit = newVatOption.id
    }
})

const isVatEditable = computed(() => isDebitVatEditable(line.value))

</script>

<style scoped>

</style>